import { useEffect, useState } from 'react';
import {
  Loader2, RefreshCw, Flame, AlertTriangle, Mail, Trophy, TrendingUp,
  Target, ChevronRight, Zap,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-ai`;

const LEAGUES = [
  { key: 'nfl', label: 'NFL' },
  { key: 'cfb', label: 'College Football' },
  { key: 'nba', label: 'NBA' },
];

function ConfidencePill({ value }) {
  const n = Number(value) || 0;
  const color =
    n >= 85 ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' :
    n >= 70 ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' :
    'bg-sky-500/20 text-sky-300 border-sky-500/40';
  return <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-mono ${color}`}>{n}%</span>;
}

function GamePickCard({ pick, onEmail, idx }) {
  const result = pick.result;
  const resultColor = result === 'won' ? 'bg-emerald-500/10 border-emerald-500/40' :
                      result === 'lost' ? 'bg-red-500/10 border-red-500/40' :
                      result === 'push' ? 'bg-sky-500/10 border-sky-500/40' :
                      'bg-zinc-900/60 border-zinc-800';
  return (
    <div className={`rounded-xl border p-3 ${resultColor}`} data-testid={`game-pick-${idx}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">{pick.pick_type}</span>
          <ConfidencePill value={pick.confidence} />
          {pick.blowout_alert && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-orange-500/20 text-orange-300 border-orange-500/40 flex items-center gap-1">
              <Flame className="w-3 h-3" /> Blowout
            </span>
          )}
          {pick.upset_risk && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-red-500/20 text-red-300 border-red-500/40 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Upset Risk
            </span>
          )}
          {result && (
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-bold ${result === 'won' ? 'bg-emerald-500 text-black' : result === 'lost' ? 'bg-red-500 text-white' : 'bg-sky-500 text-white'}`}>
              {result}
            </span>
          )}
        </div>
        <button onClick={() => onEmail(idx)} className="text-xs text-amber-300 hover:text-amber-200 flex items-center gap-1" data-testid={`email-game-${idx}`}>
          <Mail className="w-3 h-3" /> Email me
        </button>
      </div>
      <p className="text-sm text-zinc-400 mt-2">{pick.matchup}</p>
      <p className="text-lg text-white font-bold mt-0.5">{pick.selection}</p>
      <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{pick.reasoning}</p>
      {pick.actual_score && <p className="text-[10px] text-zinc-500 mt-1 font-mono">Final: {pick.actual_score}</p>}
    </div>
  );
}

function PlayerPropCard({ prop, onEmail, idx }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3" data-testid={`prop-${idx}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">{(prop.prop_type || '').replace('_', ' ')}</span>
          <ConfidencePill value={prop.confidence} />
          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${prop.pick === 'OVER' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' : 'bg-red-500/15 text-red-300 border-red-500/40'}`}>
            {prop.pick}
          </span>
        </div>
        <button onClick={() => onEmail(idx)} className="text-xs text-amber-300 hover:text-amber-200 flex items-center gap-1" data-testid={`email-prop-${idx}`}>
          <Mail className="w-3 h-3" /> Email
        </button>
      </div>
      <p className="text-sm text-white font-bold mt-2">
        {prop.player_name} <span className="text-zinc-500 font-normal">({prop.team} vs {prop.opponent})</span>
      </p>
      <p className="text-base text-amber-300 font-mono font-bold">
        {prop.pick} {prop.line} {(prop.prop_type || '').replace('_', ' ')}
      </p>
      <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{prop.reasoning}</p>
    </div>
  );
}

function ParlayPackCard({ pack, onEmail, idx }) {
  const nameColor =
    (pack.name || '').toLowerCase().includes('safe') ? 'text-emerald-300' :
    (pack.name || '').toLowerCase().includes('moon') ? 'text-fuchsia-300' :
    'text-amber-300';
  return (
    <div className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-950/30 to-zinc-900 p-3" data-testid={`pack-${idx}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className={`text-sm font-bold ${nameColor}`}>{pack.name}</p>
          <p className="text-xs text-zinc-500 font-mono">
            {pack.legs?.length || 0} legs → <span className="text-emerald-300 font-bold">{pack.combined_odds || 'N/A'}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ConfidencePill value={pack.confidence} />
          <button onClick={() => onEmail(idx)} className="text-xs text-amber-300 hover:text-amber-200 flex items-center gap-1" data-testid={`email-pack-${idx}`}>
            <Mail className="w-3 h-3" /> Email
          </button>
        </div>
      </div>
      <div className="mt-2 space-y-1">
        {(pack.legs || []).map((leg, i) => (
          <div key={i} className="text-xs text-zinc-300 flex items-start gap-1.5">
            <ChevronRight className="w-3 h-3 text-zinc-600 flex-shrink-0 mt-0.5" />
            <span className="flex-1">{leg.description}</span>
            <span className="text-zinc-500 font-mono">{leg.odds}</span>
          </div>
        ))}
      </div>
      {pack.reasoning && <p className="text-[10px] text-zinc-500 mt-2 italic leading-relaxed">{pack.reasoning}</p>}
    </div>
  );
}

function AccuracyBar({ accuracy, league }) {
  const cats = [
    { key: 'spread', label: 'Spreads' },
    { key: 'total', label: 'Totals' },
    { key: 'moneyline', label: 'Moneyline' },
    { key: 'prop', label: 'Props' },
  ];
  const empty = (accuracy?.last_4_weeks_total || 0) === 0;
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3" data-testid="accuracy-bar">
      <div className="flex items-center gap-2 mb-2">
        <Target className="w-4 h-4 text-emerald-300" />
        <p className="text-sm font-semibold text-white">Model Accuracy — last 4 weeks ({(league || '').toUpperCase()})</p>
      </div>
      {empty ? (
        <p className="text-xs text-zinc-500">No graded picks yet — accuracy will appear here once games finish and you tap "Grade finished games".</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {cats.map((c) => {
            const w = accuracy[`${c.key}_wins`] || 0;
            const l = accuracy[`${c.key}_losses`] || 0;
            const rate = accuracy[`${c.key}_hit_rate`] || 0;
            if (w + l === 0) return null;
            const color = rate >= 60 ? 'text-emerald-300' : rate >= 50 ? 'text-amber-300' : 'text-red-300';
            return (
              <div key={c.key} className="rounded-md bg-zinc-950 border border-zinc-800 p-2">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">{c.label}</p>
                <p className={`text-base font-bold ${color}`}>{rate.toFixed(0)}%</p>
                <p className="text-[10px] text-zinc-600">{w}W · {l}L</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AIPicksTab({ fixedLeague = null }) {
  const [league, setLeague] = useState(fixedLeague || 'nfl');
  const [picks, setPicks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [grading, setGrading] = useState(false);

  const loadCached = async (lg) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/picks?league=${lg}`);
      setPicks(res.data);
    } catch (e) {
      toast.error(`Load failed: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadCached(league); }, [league]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/picks/generate`, { league });
      setPicks({ ...res.data, has_picks: true });
      toast.success(`Fresh picks generated for ${league.toUpperCase()}`);
    } catch (e) {
      toast.error(`Generate failed: ${e.response?.data?.detail || e.message}`);
    } finally { setGenerating(false); }
  };

  const emailPick = async (kind, index) => {
    try {
      await axios.post(`${API}/picks/email`, { league, kind, index });
      toast.success('📧 Pick emailed to clearallcreditrepair@gmail.com');
    } catch (e) {
      toast.error(`Email failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const gradeAll = async () => {
    setGrading(true);
    try {
      const res = await axios.post(`${API}/picks/grade`, { league });
      toast.success(`Graded ${res.data.graded} completed pick${res.data.graded === 1 ? '' : 's'}`);
      await loadCached(league);
    } catch (e) { toast.error(e.message); } finally { setGrading(false); }
  };

  return (
    <div className="space-y-4" data-testid="ai-picks-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-xl p-1">
          {LEAGUES.filter((lg) => !fixedLeague || lg.key === fixedLeague).map((lg) => (
            <button
              key={lg.key}
              onClick={() => setLeague(lg.key)}
              className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors ${league === lg.key ? 'bg-amber-500 text-black' : 'text-zinc-400 hover:text-white'}`}
              data-testid={`league-${lg.key}`}
            >
              {lg.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={gradeAll} disabled={grading} variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid="grade-btn">
            {grading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-1" />}
            Grade finished games
          </Button>
          <Button size="sm" onClick={generate} disabled={generating} className="bg-amber-500 hover:bg-amber-600 text-black font-semibold" data-testid="generate-btn">
            {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
            {picks?.has_picks ? 'Refresh picks' : 'Generate picks'}
          </Button>
        </div>
      </div>

      <AccuracyBar accuracy={picks?.accuracy || {}} league={league} />

      {loading ? (
        <div className="rounded-xl border border-dashed border-zinc-800 p-10 text-center">
          <Loader2 className="w-6 h-6 text-amber-300 animate-spin mx-auto" />
        </div>
      ) : !picks?.has_picks ? (
        <div className="rounded-xl border border-dashed border-zinc-800 p-10 text-center">
          <Trophy className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
          <p className="text-sm text-zinc-400 mb-3">No picks for {league.toUpperCase()} yet this week.</p>
          <Button onClick={generate} disabled={generating} className="bg-amber-500 hover:bg-amber-600 text-black font-semibold">
            <Zap className="w-4 h-4 mr-1" /> Generate picks
          </Button>
          <p className="text-[10px] text-zinc-500 mt-3">LLM analyzes this week's slate + odds. Takes 15-30 seconds.</p>
        </div>
      ) : (
        <>
          {(picks.game_picks || []).length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                <Target className="w-4 h-4 text-amber-300" /> Game Picks ({picks.game_picks.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {picks.game_picks.map((p, i) => (
                  <GamePickCard key={i} pick={p} idx={i} onEmail={(idx) => emailPick('game_pick', idx)} />
                ))}
              </div>
            </section>
          )}

          {(picks.player_props || []).length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                <Flame className="w-4 h-4 text-orange-300" /> Player Props ({picks.player_props.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {picks.player_props.map((p, i) => (
                  <PlayerPropCard key={i} prop={p} idx={i} onEmail={(idx) => emailPick('player_prop', idx)} />
                ))}
              </div>
            </section>
          )}

          {(picks.parlay_packs || []).length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-fuchsia-300" /> Suggested Parlay Packs ({picks.parlay_packs.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {picks.parlay_packs.map((p, i) => (
                  <ParlayPackCard key={i} pack={p} idx={i} onEmail={(idx) => emailPick('parlay_pack', idx)} />
                ))}
              </div>
            </section>
          )}

          <p className="text-[10px] text-zinc-600 italic text-center mt-6">
            For entertainment only. Bet responsibly. Confidence scores are AI estimates — not guarantees.
          </p>
        </>
      )}
    </div>
  );
}
